import { AnimatePresence, motion } from 'framer-motion'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useGetWindowWidth } from '../../lib/hooks/useGetWindowWidth'
import CustomLink from './CustomLink'
import { User } from '../../lib/types/User'
import { useDispatch, useSelector } from 'react-redux'
import { currentUserSelector } from '../../store/selectors/userSelector'
import DealershipSelect from '../DealershipSelect'
import { ReactComponent as ChevronRight } from './../../images/icons/chevron_right.svg'
import { ReactComponent as DealerProfileIcon } from './../../images/icons/location.svg'
import { ReactComponent as CampaignsIcon } from './../../images/icons/icon_chart.svg'
import { ReactComponent as CustomersIcon } from './../../images/icons/customers.svg'
import { ReactComponent as AudiencesIcon } from './../../images/icons/audiences_icon.svg'
import { ReactComponent as InventoryIcon } from './../../images/icons/inventory.svg'
import { ReactComponent as ReportsIcon } from './../../images/icons/service_settings.svg'
import { ReactComponent as UserProfileIcon } from './../../images/icons/user_profile.svg'
import { ReactComponent as AdminIcon } from './../../images/icons/admin_icon.svg'
import { isEqual } from 'lodash'
import { setAuthenticated, setUser } from '../../store/reducers/userReducer'
import { useGetUser } from '../../api/auth/useGetUser'
import { useAuthLogout } from '../../api/auth/useAuthLogout'
import { arrow, autoUpdate, useFloating } from '@floating-ui/react-dom'
import { Link } from 'react-router-dom'
import { useParams } from 'react-router'
import { SideMenu } from '@demandlocal/components'
import { useDealership } from '../../store/zustand/dealershipStore'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import sidebar from '../../localization/en/sidebar'
import { GA4 } from '../../lib/utils/ga4'
import { getImageURL } from '../../lib/utils/getImageURL'
import { useGetAgencyByAgencyId } from '../../api/agencies/useGetAgencyByAgencyId'
import mtm from '../../lib/utils/mtm'
import AgencyFlyout from '../AgencyFlyout'
import NotificationsBell from '../Notifications/NotificationsBell'

export type MenuItem = {
  name: string
  path: string
  icon?: JSX.Element
  children?: Array<MenuItem>
  hidden?: boolean
}

const menu = (
  t: TFunction<'sidebar', keyof typeof sidebar>,
  options: {
    industry: string
    is_tier_2?: boolean
    isPathToConversionReportEnabled?: boolean
    isConversionReportEnabled?: boolean
    isCreativePerformanceReportEnabled?: boolean
    isPolkReportEnabled?: boolean
    isSalePathReportEnabled?: boolean
    isGA4ReportEnabled?: boolean
    currentUser?: User | null
  } = {
    industry: 'automotive',
    is_tier_2: false,
    isPathToConversionReportEnabled: false,
    isConversionReportEnabled: false,
    isCreativePerformanceReportEnabled: false,
    isPolkReportEnabled: false,
    isSalePathReportEnabled: false,
    isGA4ReportEnabled: false,
    currentUser: undefined,
  },
): Array<MenuItem> => {
  const storePath =
    {
      automotive: 'dealership',
      retail: 'account',
    }[options.industry] ?? 'dealership'

  return [
    {
      name: t('menu.profile'),
      path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/profile`,
      icon: <DealerProfileIcon className={'mr-2 w-5 h-5 stroke-white'} />,
    },
    {
      name: t('menu.customers.customers'),
      path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/customers`,
      icon: <CustomersIcon className={'mr-2'} />,
      children: [
        {
          name: t('menu.customers.list'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/customers/list`,
          // hidden: true,
        },
        {
          name: t('menu.customers.sources'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/customers/sources/list`,
        },
      ],
    },
    {
      name: t('menu.audiences.audiences'),
      path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/audiences`,
      icon: <AudiencesIcon className="mr-2 w-5 h-5 stroke-white" />,
      children: [
        {
          name: t('menu.audiences.tag'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/audiences/tag`,
        },
        {
          name: t('menu.audiences.programmatic'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/audiences/programmatic`,
        },
        {
          name: t('menu.audiences.custom'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/audiences/custom`,
        },
      ],
    },
    {
      name: t('menu.inventory.inventory'),
      path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/inventory`,
      icon: <InventoryIcon className={'mr-2'} />,
      hidden: options?.industry === 'retail',
      children: [
        {
          name: t('menu.inventory.overview'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/inventory/overview`,
        },
        {
          name: t('menu.inventory.list'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/inventory/list`,
        },
        {
          name: t('menu.inventory.marketed'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/inventory/marketed`,
        },
        {
          name: t('menu.inventory.sold'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/inventory/sold-vehicles`,
        },
      ],
    },
    {
      name: t('menu.campaigns.campaigns'),
      path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/campaigns`,
      icon: <CampaignsIcon className={'mr-2 w-5 h-5 fill-white'} />,
      children: [
        {
          name: t('menu.campaigns.managed'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/campaigns/list`,
        },
        {
          name: t('menu.campaigns.selfService'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/campaigns/self-service/list`,
        },
        {
          name: t('menu.campaigns.monthOverMonth'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/campaigns/breakdown`,
        },
      ],
    },
    {
      name: t('menu.reports.reports'),
      path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports`,
      icon: <ReportsIcon className={'mr-2 w-5 h-5 stroke-white'} />,
      children: [
        {
          name: t('menu.reports.conversions'),
          hidden: !options.isConversionReportEnabled,
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports/conversions`,
        },
        {
          name: t('menu.reports.creatives'),
          hidden: !options.isCreativePerformanceReportEnabled,
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports/creatives`,
        },
        {
          name: t('menu.reports.conversionPaths'),
          hidden: !options.isPathToConversionReportEnabled,
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports/conversion-paths`,
        },
        {
          hidden: Boolean(!options.isSalePathReportEnabled),
          name: t('menu.reports.salePaths'),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports/sale-paths`,
        },
        {
          name: t('menu.reports.polk'),
          hidden: Boolean(!(options.isPolkReportEnabled || options.is_tier_2)),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports/verified-sales`,
        },
        {
          name: t('menu.reports.ga4'),
          hidden: Boolean(!options.isGA4ReportEnabled),
          path: `/dashboard/agency/:agencyId/${storePath}/:dealerId/reports/ga4`,
        },
      ],
    },

    {
      name: 'Admin',
      path: '/dashboard/admin',
      icon: <AdminIcon className={'mr-2'} />,
      hidden: options?.currentUser?.role_id !== 1,
    },
  ]
}

const LeftBarMenu = (): JSX.Element => {
  const { windowWidth } = useGetWindowWidth()
  const [isOpened, setIsOpened] = useState(true)

  const { data, isLoading } = useGetUser()
  const dispatch = useDispatch()
  const user: User | null = useSelector(currentUserSelector)
  const currentDealership = useDealership()

  const { t } = useTranslation('sidebar', {
    keyPrefix: currentDealership?.industry || 'automotive',
  })

  useEffect(() => {
    if (!isLoading && !isEqual(user, data)) dispatch(setUser(data))
  }, [data, dispatch, isLoading, user])

  useLayoutEffect(() => {
    if (windowWidth < 1280) {
      if (isOpened) setIsOpened(false)
    } else {
      if (!isOpened) setIsOpened(true)
    }
  }, [isOpened, windowWidth])

  const { mutate: logout } = useAuthLogout()

  const handleLogout = useCallback(() => {
    logout()
    dispatch(setAuthenticated(false))
  }, [dispatch, logout])

  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const arrowRef = useRef(null)
  const { reference, floating, refs, update } = useFloating({
    placement: 'right-end',
    middleware: [arrow({ element: arrowRef })],
  })

  useEffect(() => {
    if (!refs.reference.current || !refs.floating.current) return

    // Only call this when the floating element is rendered
    return autoUpdate(refs.reference.current, refs.floating.current, update)
  }, [refs.reference, refs.floating, update])

  useEffect(() => {
    const handleClick = (): void => {
      setIsMenuOpen(false)
    }
    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [])

  const { agency_id } = useParams()
  const { data: agency, isLoading: loadingAgency } = useGetAgencyByAgencyId(
    agency_id?.toString(),
  )
  const getAgencyLogo = useCallback(() => {
    if (loadingAgency) return <div className="w-32 h-24"></div>

    if (agency_id && agency && agency?.has_logo) {
      const s3Bucket = 'link-logos'
      const fileName = `${
        process.env.NODE_ENV !== 'production' ? 'test/' : ''
      }agency_${agency_id}`
      return (
        <img
          className={'w-32 h-24 object-contain'}
          src={`https://${s3Bucket}.s3.amazonaws.com/${fileName}`}
        />
      )
    } else {
      return (
        <img
          className={'w-32 h-24 object-contain'}
          src={getImageURL('images/logo_vertical.svg')}
        />
      )
    }
  }, [agency_id, agency, loadingAgency])

  const menuRef = useRef(null)
  const topRef = useRef(null)
  const bottomRef = useRef(null)

  const [menuTotalHeight, setMenuTotalHeight] = useState<number>(0)

  useEffect(() => {
    const observer = new ResizeObserver((e) => {
      setMenuTotalHeight(e[0].target.clientHeight)
    })
    const element = menuRef.current as unknown as Element
    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [])

  const menuHeight = useMemo(() => {
    if (topRef.current && bottomRef.current && menuTotalHeight > 0) {
      return (
        (bottomRef.current as Element).getBoundingClientRect().top -
        (topRef.current as Element).getBoundingClientRect().bottom
      )
    } else {
      return 0
    }
  }, [menuTotalHeight])

  return (
    <div>
      <div>
        <SideMenu logo={getAgencyLogo()}>
          <div className={'flex-1'} ref={menuRef}>
            {user?.beta_tester && (
              <div className="flex flex-row items-center gap-2 p-5 border-b-2 border-space-cadet">
                <input
                  type="text"
                  className="w-full h-10 rounded-md bg-primary-independence/60 px-2"
                  placeholder="Search..."
                />
                <NotificationsBell />
              </div>
            )}
            {(user?.role_id ?? 3) < 3 ? <AgencyFlyout /> : <></>}
            <div className={'pt-5 px-5 flex flex-col gap-4'} ref={topRef}>
              <div className={'text-primary-glaucous text-[10px] font-bold uppercase'}>
                {t('header')}
              </div>
              <DealershipSelect />
            </div>
            <div className="flex flex-col overflow-hidden" style={{ height: menuHeight }}>
              <div
                className={
                  'font-Spartan font-bold w-full px-5 pt-5 pb-5 overflow-y-scroll'
                }>
                {menu(t, {
                  industry: currentDealership?.industry ?? 'automotive',
                  is_tier_2: agency?.is_tier_2,
                  isPathToConversionReportEnabled:
                    currentDealership?.enable_path_to_conversion_report ?? false,
                  isConversionReportEnabled:
                    currentDealership?.enable_conversion_report ?? false,
                  isCreativePerformanceReportEnabled:
                    currentDealership?.enable_creative_report ?? false,
                  isGA4ReportEnabled: currentDealership?.enable_ga4_report ?? false,
                  isSalePathReportEnabled:
                    currentDealership?.enable_path_to_sale_report ?? false,
                  currentUser: user,
                }).map((item, index) => {
                  if (item.hidden) return null
                  return (
                    <CustomLink
                      className={index > 0 ? 'pt-5' : ''}
                      key={index}
                      name={item.name}
                      to={item.path}
                      icon={item.icon}
                      subMenuItems={item.children}
                      onClick={() => {
                        GA4.sendEvent({
                          event_name: 'menu_click',
                          event_params: item.name,
                        })
                        mtm.sendEvent('Menu', 'Click', item.name)
                      }}
                    />
                  )
                })}
              </div>
            </div>
          </div>
          <div
            className={
              'border-t-2 w-full py-5 px-5 flex flex-col gap-3 border-space-cadet fixed bottom-0 left-0'
            }
            ref={bottomRef}>
            <div
              className="flex flex-1 items-center rounded-md py-3 px-2 font-bold text-xs fill-red-500 text-white cursor-pointer"
              onClick={(e) => setIsMenuOpen((prev) => !prev)}>
              <UserProfileIcon className={'mr-2 fill-current'} />
              <div className={'flex flex-1 justify-between items-center'}>
                <div>{`${user?.first_name} ${user?.last_name}`}</div>
                <div
                  className="ml-auto w-6 h-6 rounded-full bg-space-cadet"
                  ref={reference}>
                  <ChevronRight className={'fill-current'} />
                </div>
              </div>
            </div>
          </div>
        </SideMenu>
      </div>
      <AnimatePresence presenceAffectsLayout={true}>
        {isMenuOpen && (
          <motion.div
            ref={floating}
            initial={{ x: 0, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ y: 0, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30,
              mass: 0.5,
              velocity: 0.5,
            }}
            className={classNames(
              'flex items-start flex-col w-40 rounded-xl p-3 drop-shadow-xl font-Spartan text-xs bg-primary-xiketic z-[1000000] fixed left-[257px] bottom-[20px]',
            )}>
            <div
              className={
                'w-4 h-4 [left:-0.5rem] fixed bottom-[16px] transform transform-transition duration-200 rotate-45 bg-primary-xiketic'
              }
              ref={arrowRef}
            />
            <div className={'py-2 group cursor-pointer w-full'}>
              <Link
                to={'https://form.asana.com/?k=FEMVzYxoz33bWRhfdf8x6A&d=189525429620912'}
                target="_blank"
                rel="noopener noreferrer"
                className={
                  'font-medium text-white opacity-60 group-hover:opacity-100 group-hover:font-bold'
                }>
                Contact Us
              </Link>
            </div>
            <div className={'py-2 group cursor-pointer w-full'}>
              <Link
                to={'/dashboard/user/profile'}
                className={
                  'font-medium text-white opacity-60 group-hover:opacity-100 group-hover:font-bold'
                }>
                Edit Profile
              </Link>
            </div>

            <div className={'py-2 group cursor-pointer w-full'} onClick={handleLogout}>
              <div
                className={
                  'font-medium text-white opacity-60 group-hover:opacity-100 group-hover:font-bold'
                }>
                Logout
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LeftBarMenu
